import React, { isValidElement, useEffect, useId, useRef, useState } from "react";
import {
  type ButtonProps as AriaButtonProps,
  Tooltip as AriaTooltip,
  type TooltipProps as AriaTooltipProps,
  composeRenderProps,
  OverlayArrow,
  type PressEvent,
  TooltipTrigger,
} from "react-aria-components";

/** A text tooltip shown on hover or focus, with optional click/touch access. */
export interface TooltipProps extends Omit<AriaTooltipProps, "children" | "isOpen" | "defaultOpen" | "onOpenChange"> {
  children: React.ReactNode;
  content: React.ReactNode;
  /** Allow an interactive trigger to pin the tooltip open by pressing it. */
  openOnPress?: boolean;
  delay?: number;
  closeDelay?: number;
  isDisabled?: boolean | undefined;
}

const styles = ({
  isEntering,
  isExiting,
  className,
}: {
  isEntering?: boolean;
  isExiting?: boolean;
  className?: string;
}) => {
  const baseClasses =
    "max-w-xs group bg-background-surface border border-border-default text-text-primary text-sm leading-relaxed rounded-lg shadow-lg will-change-transform px-3 py-2";

  const enteringClasses = isEntering
    ? "animate-in fade-in placement-bottom:slide-in-from-top-0.5 placement-top:slide-in-from-bottom-0.5 placement-left:slide-in-from-right-0.5 placement-right:slide-in-from-left-0.5 ease-out duration-200"
    : "";

  const exitingClasses = isExiting
    ? "animate-out fade-out placement-bottom:slide-out-to-top-0.5 placement-top:slide-out-to-bottom-0.5 placement-left:slide-out-to-right-0.5 placement-right:slide-out-to-left-0.5 ease-in duration-150"
    : "";

  return [baseClasses, enteringClasses, exitingClasses, className].filter(Boolean).join(" ");
};

type OverlayProps = Omit<TooltipProps, "children" | "openOnPress" | "delay" | "closeDelay" | "isDisabled">;
type HoverEvent = Parameters<NonNullable<AriaButtonProps["onHoverStart"]>>[0];

function TooltipOverlay({ content, tooltipRef, ...props }: OverlayProps & { tooltipRef?: React.Ref<HTMLDivElement> }) {
  return (
    <AriaTooltip
      {...props}
      ref={tooltipRef}
      offset={props.offset ?? 8}
      className={composeRenderProps(props.className, (className, renderProps) => styles({ ...renderProps, className }))}
    >
      <OverlayArrow>
        <svg
          width={8}
          height={8}
          viewBox="0 0 8 8"
          className="fill-background-surface stroke-border-default group-placement-left:-rotate-90 group-placement-right:rotate-90 group-placement-bottom:rotate-180 forced-colors:fill-[Canvas] forced-colors:stroke-[ButtonBorder]"
        >
          <path d="M0 0 L4 4 L8 0" />
        </svg>
      </OverlayArrow>
      {content}
    </AriaTooltip>
  );
}

type PressableChildProps = {
  onPress?: (event: PressEvent) => void;
  onHoverStart?: (event: HoverEvent) => void;
  onHoverEnd?: (event: HoverEvent) => void;
  "data-tooltip-trigger"?: string;
};

function PressTooltip({
  children,
  content,
  delay = 200,
  closeDelay,
  isDisabled,
  ...props
}: Omit<TooltipProps, "openOnPress">) {
  const [isOpen, setIsOpen] = useState(false);
  const pinned = useRef(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerId = useId();

  useEffect(() => {
    if (!isOpen) return;

    const close = () => {
      pinned.current = false;
      setIsOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) return;
      const trigger = document.querySelector(`[data-tooltip-trigger="${triggerId}"]`);
      if (trigger?.contains(event.target) || tooltipRef.current?.contains(event.target)) return;
      close();
    };

    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [isOpen, triggerId]);

  useEffect(
    () => () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
    },
    []
  );

  if (!isValidElement<PressableChildProps>(children)) {
    throw new Error("Tooltip with openOnPress requires a single pressable child");
  }

  const trigger = React.cloneElement(children, {
    "data-tooltip-trigger": triggerId,
    onHoverStart: (event: HoverEvent) => {
      children.props.onHoverStart?.(event);
      hoverTimer.current = setTimeout(() => setIsOpen(true), delay);
    },
    onHoverEnd: (event: HoverEvent) => {
      children.props.onHoverEnd?.(event);
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
      if (!pinned.current) setIsOpen(false);
    },
    onPress: (event: PressEvent) => {
      children.props.onPress?.(event);
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
      pinned.current = !pinned.current;
      setIsOpen(pinned.current);
    },
  });

  return (
    <TooltipTrigger
      delay={delay}
      closeDelay={closeDelay}
      shouldCloseOnPress={false}
      isDisabled={isDisabled}
      isOpen={isOpen && !isDisabled}
      onOpenChange={(open) => {
        if (!open && pinned.current) return;
        setIsOpen(open);
      }}
    >
      {trigger}
      <TooltipOverlay {...props} content={content} tooltipRef={tooltipRef} />
    </TooltipTrigger>
  );
}

export function Tooltip({
  children,
  content,
  openOnPress = false,
  delay = 200,
  closeDelay,
  isDisabled,
  ...props
}: TooltipProps) {
  if (openOnPress) {
    return (
      <PressTooltip {...props} content={content} delay={delay} closeDelay={closeDelay} isDisabled={isDisabled}>
        {children}
      </PressTooltip>
    );
  }

  return (
    <TooltipTrigger delay={delay} closeDelay={closeDelay} isDisabled={isDisabled}>
      {children}
      <TooltipOverlay {...props} content={content} />
    </TooltipTrigger>
  );
}
